/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';
import { InformativeIcon, WarningFilledIcon } from '@coursera/cds-icons';

import { useEnterpriseOrgScopedModalManager } from 'bundles/unified-common/providers/org-scoped/modals/EnterpriseOrgScopedModalManagerContext';

import _t from 'i18n!nls/unified-home-common';

const styles = {
  previewBannerWrapper: css`
    background-color: var(--cds-color-emphasis-tertiary-background-xweak);
    align-items: center;
    flex-wrap: nowrap;
  `,
  previewBannerWraperExited: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
  `,
  icon: css`
    line-height: 0;
    flex-grow: 0;
    flex-shrink: 0;

    svg {
      height: 100%;
      width: 100%;
      max-height: 100%;
    }
  `,
  previewBannerElements: css`
    align-items: center;
    flex-wrap: nowrap;
    justify-content: space-between;
  `,
  pushExitButtonDownInMobile: css`
    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  previewTextWithIcon: css`
    flex-wrap: nowrap;
    align-items: center;
    padding: var(--cds-spacing-200) 0;
    width: auto;
  `,
  previewText: css`
    flex-wrap: nowrap;
    align-items: center;
    vertical-align: top;
    width: auto;
  `,
  changeProgramBtn: css`
    text-decoration: underline;
    display: inline;

    &,
    &:hover {
      background-color: transparent;
      padding: 0;
      font-weight: normal;
      vertical-align: unset;
    }

    &:hover {
      text-decoration: none;
    }
  `,
  exitPreviewBtn: css`
    margin-left: var(--cds-spacing-150);
    white-space: nowrap;

    ${breakpoints.down('xs')} {
      margin-left: var(--cds-border-radius-400);
      margin-bottom: var(--cds-spacing-300);
    }
  `,
};
type Props = {
  programNames?: string[];
  onExitPreviewMode?: () => void;
};

const OrgScopedAdminImpersonationBanner = ({ programNames, onExitPreviewMode }: Props) => {
  const modalManager = useEnterpriseOrgScopedModalManager();

  const hasNoProgramImpersonations = !programNames || programNames.length === 0;

  const safeProgramNames = programNames ?? [];
  const firstTwoNames = safeProgramNames.slice(0, 2);
  const additionalProgramNamesCount = safeProgramNames.length > 2 ? safeProgramNames.length - 2 : 0;

  // Not all languages use commas to separate nouns in a mid-sentence list.
  const twoProgramNamesTranslated = _t('#{programName1}, #{programName2}', {
    programName1: firstTwoNames[0],
    programName2: firstTwoNames[1],
  });

  return (
    <Grid
      container
      className="rc-PreviewModeBanner"
      data-testid="preview-mode-banner"
      css={[styles.previewBannerWrapper, hasNoProgramImpersonations && styles.previewBannerWraperExited]}
    >
      <PageGridContainer>
        <Grid
          container
          item
          css={[styles.previewBannerElements, !hasNoProgramImpersonations && styles.pushExitButtonDownInMobile]}
        >
          <Grid item container css={styles.previewTextWithIcon} spacing={12}>
            <Grid item css={styles.icon}>
              {!hasNoProgramImpersonations ? <WarningFilledIcon /> : <InformativeIcon color="interactive" />}
            </Grid>
            <Grid item css={styles.previewText}>
              <Typography2 component="span" variant="bodyPrimary">
                {!hasNoProgramImpersonations ? (
                  <FormattedMessage
                    message={_t(
                      'This is only a preview mode. You’re previewing the learner experience for: {programNames}{numAdditionalPrograms, plural, =0 {} other {, +# more}}.'
                    )}
                    programNames={safeProgramNames.length > 1 ? twoProgramNamesTranslated : safeProgramNames[0]}
                    numAdditionalPrograms={additionalProgramNamesCount}
                  />
                ) : (
                  <Typography2 component="span" variant="bodyPrimary">
                    {_t('You are viewing content based on your program memberships.')}
                  </Typography2>
                )}
                {/* Needs a separator of some kind so the change program button doesn't crowd out the previous sentence */}
                &nbsp;
                <Button
                  variant="ghost"
                  css={styles.changeProgramBtn}
                  onClick={() => modalManager?.setShowChangePreviewProgramsModal(true)}
                >
                  {_t('Change programs')}
                </Button>
              </Typography2>
            </Grid>
          </Grid>

          <Grid className="preview-mode-exit" item>
            {!hasNoProgramImpersonations && (
              <Button variant="primary" size="small" css={styles.exitPreviewBtn} onClick={onExitPreviewMode}>
                {_t('Exit preview')}
              </Button>
            )}
          </Grid>
        </Grid>
      </PageGridContainer>
    </Grid>
  );
};

export default OrgScopedAdminImpersonationBanner;
